// Public UI adapted from Klargo-Reference/pages/LandingPreLogin.tsx.
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "../adapters/Helmet.jsx";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion.jsx";
import { useLanguage } from "../components/layout/LanguageSwitcher.jsx";
import ServiceLogoRow from "../components/landing/ServiceLogoRow.jsx";
import HeroArtifacts, { HERO_DEAL } from "../components/landing/HeroArtifacts.jsx";
import { Menu, X } from "lucide-react";
import { LoginDialog } from "../components/auth/LoginDialog.jsx";
import LanguageSwitcher from "../components/layout/LanguageSwitcher.jsx";
import { KlargoLogo } from "../components/common/KlargoLogo.jsx";
import RegistrationPlate from "../components/common/RegistrationPlate.jsx";
import DsLogo from "../components/ds/DsLogo.jsx";
import { DownloadMark, PdfFileMark } from "../components/ds/icons.jsx";
import { ForwardArrow, StatusDot, VerifiedTick } from "../components/ds/StatusMark.jsx";
import { formatDateLocalized } from "../lib/format.jsx";
import Footer from "../components/layout/Footer.jsx";
import VehicleTypeVisual from "../components/vehicles/VehicleTypeVisual.jsx";
const SCROLL_THROTTLE_MS = 100;
const STAGE_RULES = [
    "border-b md:border-b-0 md:pr-8",
    "border-b md:border-b-0 md:border-l md:pl-8 lg:pr-8",
    "border-b md:border-b-0 md:border-t md:pr-8 md:pt-7 lg:border-t-0 lg:border-l lg:pl-8 lg:pt-5",
    "md:border-t md:border-l md:pl-8 md:pt-7 lg:border-t-0 lg:pt-5",
];
const SectionHeader = ({ kicker, title, lead, className, }) => (<div className={["mb-9 max-w-[640px]", className].filter(Boolean).join(" ")}>
    <div className="mb-1.5 text-[13px] font-semibold tracking-[0.02em] text-ds-muted">{kicker}</div>
    <h2 className="m-0 font-ds-serif text-[clamp(24px,2.6vw,30px)] font-bold leading-[1.12] tracking-[-0.01em] text-ds-ink">
      {title}
    </h2>
    {lead && <p className="mb-0 mt-3 text-[15px] leading-[1.55] text-ds-text">{lead}</p>}
  </div>);
const GhostRecord = ({ className }) => (<div className={`absolute rounded-[20px] border border-[rgba(11,122,85,.05)] ${className ?? ""}`} style={{
        background: "linear-gradient(158deg, rgba(255,255,255,.5), rgba(255,255,255,.16) 54%, rgba(11,122,85,.018) 90%)",
        boxShadow: "0 54px 96px -68px rgba(16,30,40,.16)",
    }}/>);
const TransactionTrace = ({ className, drawn = true, }) => (<svg className={`landing-trace ${className ?? ""}`} data-drawn={drawn ? "true" : "false"} viewBox="0 0 440 168" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="klargo-trace" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#0B7A55" stopOpacity="0"/>
        <stop offset="22%" stopColor="#0B7A55" stopOpacity=".1"/>
        <stop offset="62%" stopColor="#0B7A55" stopOpacity=".09"/>
        <stop offset="100%" stopColor="#0B7A55" stopOpacity="0"/>
      </linearGradient>
    </defs>
    <path d="M4 150 H104 L142 108 H250 L288 62 H436" stroke="url(#klargo-trace)" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="104" cy="150" r="3" fill="#0B7A55" fillOpacity=".12"/>
    <circle cx="250" cy="108" r="4.5" stroke="#0B7A55" strokeOpacity=".13" strokeWidth="1"/>
    <circle cx="288" cy="62" r="2.5" fill="#0B7A55" fillOpacity=".1"/>
  </svg>);
const AFTER_DEAL = {
    inspectionDaysLeft: "72",
    documentCount: 3,
};
const AFTER_DEAL_HISTORY = [
    {
        reg: "DKT248",
        model: "Volvo XC60 T6 AWD",
        typeKey: "Bil",
        directionKey: "ds.transaction.soldTo",
        counterparty: "Erik Johansson",
        dateIso: "2024-11-15",
        amount: "289 000 kr",
    },
    {
        reg: "DEF456",
        model: "Yamaha MT-07",
        typeKey: "MC",
        twoRow: true,
        directionKey: "ds.transaction.boughtFrom",
        counterparty: "Maria Lindberg",
        dateIso: "2024-08-22",
        amount: "78 000 kr",
    },
];
const AfterDealArtifact = () => {
    const { t, languageCode } = useLanguage();
    const dealDate = formatDateLocalized(HERO_DEAL.dateIso, languageCode);
    const insurer = t("hero.insurer.a");
    const states = [
        {
            kicker: t("ds.section.insurance"),
            value: t("ds.insurance.active"),
            detail: insurer,
            isState: true,
        },
        {
            kicker: t("ds.section.inspection"),
            value: t("ds.inspection.approved"),
            detail: t("ds.inspection.daysLeft").replace("{days}", AFTER_DEAL.inspectionDaysLeft),
            isState: true,
        },
        {
            kicker: t("Ägare"),
            value: HERO_DEAL.buyer,
            isState: false,
        },
    ];
    const deals = [
        {
            reg: HERO_DEAL.reg,
            model: HERO_DEAL.model,
            twoRow: false,
            type: t("Bil"),
            line: t("ds.transaction.boughtFrom").replace("{counterparty}", HERO_DEAL.seller),
            date: dealDate,
            amount: HERO_DEAL.price,
        },
        ...AFTER_DEAL_HISTORY.map((deal) => ({
            reg: deal.reg,
            model: deal.model,
            twoRow: Boolean(deal.twoRow),
            type: t(deal.typeKey),
            line: t(deal.directionKey).replace("{counterparty}", deal.counterparty),
            date: formatDateLocalized(deal.dateIso, languageCode),
            amount: deal.amount,
        })),
    ];
    const documents = [
        {
            title: t("ds.document.purchaseContract"),
            source: `${t("Signerat med BankID")} ·`,
        },
        {
            title: t("ds.document.inspectionReport"),
            source: `Bilvision · ${t("ds.document.verb.retrieved")}`,
        },
        {
            title: t("ds.document.insurancePolicy"),
            source: `${insurer} · ${t("ds.document.verb.created")}`,
        },
    ];
    return (<div aria-hidden="true" className="overflow-hidden rounded-[14px] border border-ds-sheet-edge bg-ds-sheet shadow-[0_1px_2px_rgba(16,30,40,.06),0_34px_70px_-32px_rgba(16,30,40,.34)]">
      
      <div className="flex items-center gap-x-6 bg-ds-ink px-5 py-3 md:px-7">
        <DsLogo variant="mobile" tone="onInk"/>
        <div className="flex items-center gap-5 text-[13.5px] text-ds-ink-on-soft">
          <span className="border-b-2 pb-[3px] font-semibold text-white" style={{ borderColor: "var(--ds-green-nav)" }}>
            {t("nav.vehicles")}
          </span>
          <span>{t("history.title")}</span>
        </div>
      </div>

      <div className="p-5 md:p-7">
        
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <RegistrationPlate value={HERO_DEAL.reg} size="object"/>
          <div className="min-w-0">
            <div className="font-ds-serif text-[21px] font-semibold leading-[1.2] text-ds-ink">
              {HERO_DEAL.model}
            </div>
            <div className="mt-[3px] text-[13.5px] font-medium text-ds-muted">
              {t("vehicleType.car.long")}
            </div>
          </div>
        </div>
        <div className="mt-[18px] flex items-center gap-2 text-[13.5px] text-ds-text">
          <VerifiedTick />
          <span>{t("ds.vehicle.acquired.klargo").replace("{date}", dealDate)}</span>
        </div>

        
        <h3 className="m-0 mb-[7px] mt-7 font-ds-sans text-[15px] font-semibold text-ds-ink-strong">
          {t("ds.section.currentStatus")}
        </h3>
        <div className="border-b border-[rgba(20,36,46,.12)]">
          <div className="grid border-t-2 border-ds-ink sm:grid-cols-3">
            {states.map((row, index) => (<div key={row.kicker} className={`py-3.5 sm:pr-5 ${index < states.length - 1 ? "border-b border-ds-rule-row sm:border-b-0" : ""}`}>
                <div className="flex items-center gap-[7px]">
                  {row.isState && <StatusDot tone="green"/>}
                  <span className="ds-label">{row.kicker}</span>
                </div>
                <div className="mt-1 text-[17px] font-semibold leading-[1.25] text-ds-ink">
                  {row.value}
                </div>
                {row.detail && (<div className="mt-1 text-[13.5px] leading-[1.4] text-[#3E4C48] [text-wrap:pretty]">
                    {row.detail}
                  </div>)}
              </div>))}
          </div>
        </div>

        
        <div className="mt-6 rounded-[12px] border border-ds-record-field-edge bg-ds-record-field px-4 py-3 md:px-5 md:py-3.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[15px] font-semibold text-ds-ink-strong">
              {t("ds.section.documents")}
            </span>
            <span className="ds-meta">
              {t("ds.document.count.many").replace("{count}", String(AFTER_DEAL.documentCount))}
            </span>
          </div>
          <div className="mt-1">
            {documents.map((doc) => (<div key={doc.title} className="flex items-center gap-3 border-t border-ds-rule-row py-[9px] first:border-t-0 first:pt-1.5">
                <PdfFileMark width={21} height={24}/>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold leading-[1.3] text-ds-ink">
                    {doc.title}
                  </div>
                  <div className="ds-meta mt-[2px] leading-[1.4]">
                    {doc.source} <span className="whitespace-nowrap">{dealDate}</span>
                  </div>
                </div>
                <DownloadMark className="text-ds-muted" width={16} height={16}/>
              </div>))}
          </div>
        </div>

        
        <div className="mt-5 border-t border-ds-rule-row pt-4">
          <div className="flex items-baseline justify-between gap-3">
            <span className="ds-label">{t("history.title")}</span>
            <span className="ds-meta">
              {t("ds.transaction.count.many").replace("{count}", String(deals.length))}
            </span>
          </div>
          <div className="mt-0.5">
            {deals.map((deal) => (<div key={deal.reg} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t border-ds-rule-row py-2 first:border-t-0 first:pt-1">
                
                <span className="flex w-[84px] flex-none">
                  <RegistrationPlate value={deal.reg} size="micro" twoRow={deal.twoRow}/>
                </span>
                
                <div className="min-w-0 flex-1 basis-[200px]">
                  <div className="text-[14.5px] font-semibold leading-[1.3] text-ds-ink">
                    {deal.model}
                  </div>
                  <div className="ds-meta mt-[2px] leading-[1.4]">
                    {deal.type} · {deal.line} ·{" "}
                    <span className="whitespace-nowrap">{deal.date}</span>
                  </div>
                </div>
                <div className="ml-auto flex-none text-[14.5px] font-semibold tabular-nums text-ds-ink">
                  {deal.amount}
                </div>
              </div>))}
          </div>
        </div>
      </div>
    </div>);
};
const AfterDealMobile = () => {
    const { t, languageCode } = useLanguage();
    const dealDate = formatDateLocalized(HERO_DEAL.dateIso, languageCode);
    const insurer = t("hero.insurer.a");
    const dealCount = 1 + AFTER_DEAL_HISTORY.length;
    const states = [
        {
            label: t("ds.section.insurance"),
            value: t("ds.insurance.active"),
            detail: insurer,
            isState: true,
        },
        {
            label: t("ds.section.inspection"),
            value: t("ds.inspection.approved"),
            detail: t("ds.inspection.daysLeft").replace("{days}", AFTER_DEAL.inspectionDaysLeft),
            isState: true,
        },
        {
            label: t("Ägare"),
            value: HERO_DEAL.buyer,
            isState: false,
        },
    ];
    const documents = [
        { title: t("ds.document.purchaseContract"), source: `${t("Signerat med BankID")} ·` },
        { title: t("ds.document.inspectionReport"), source: `Bilvision · ${t("ds.document.verb.retrieved")}` },
        { title: t("ds.document.insurancePolicy"), source: `${insurer} · ${t("ds.document.verb.created")}` },
    ];
    return (<div aria-hidden="true" data-testid="after-deal-mobile" className="overflow-hidden rounded-[14px] border border-ds-sheet-edge bg-ds-sheet px-5 pb-5 pt-[22px] shadow-[0_1px_2px_rgba(16,30,40,.06),0_34px_70px_-32px_rgba(16,30,40,.34)]">
      
      <RegistrationPlate value={HERO_DEAL.reg} size="row"/>
      <div className="mt-3 font-ds-serif text-[21px] font-semibold leading-[1.2] text-ds-ink">
        {HERO_DEAL.model}
      </div>
      <div className="mt-[3px] text-[13.5px] font-medium text-ds-muted">
        {t("vehicleType.car.long")}
      </div>
      <div className="mt-3 flex items-start gap-2 text-[13.5px] leading-[1.45] text-ds-text">
        <VerifiedTick className="mt-[5px] shrink-0"/>
        <span>{t("ds.vehicle.acquired.klargo").replace("{date}", dealDate)}</span>
      </div>

      
      <h3 className="m-0 mt-6 border-b-2 border-ds-ink pb-[7px] font-ds-sans text-[15px] font-semibold text-ds-ink-strong">
        {t("ds.section.currentStatus")}
      </h3>
      <div className="border-b border-[rgba(20,36,46,.12)]">
        {states.map((row, index) => (<div key={row.label} className={`grid grid-cols-1 gap-x-3 gap-y-[2px] py-[9px] min-[340px]:grid-cols-[96px_minmax(0,1fr)] min-[340px]:items-baseline ${index > 0 ? "border-t border-ds-rule-row" : ""}`}>
            <div className="flex items-center gap-[7px]">
              {row.isState && <StatusDot tone="green"/>}
              <span className="ds-label">{row.label}</span>
            </div>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold leading-[1.35] text-ds-ink">{row.value}</div>
              {row.detail && (<div className="mt-[1px] text-[13.5px] leading-[1.4] text-[#3E4C48] [text-wrap:pretty]">
                  {row.detail}
                </div>)}
            </div>
          </div>))}
      </div>

      
      <div className="mt-6 flex items-baseline justify-between gap-3 pb-[5px]">
        <span className="text-[15px] font-semibold text-ds-ink-strong">
          {t("ds.section.documents")}
        </span>
        <span className="ds-meta">
          {t("ds.document.count.many").replace("{count}", String(AFTER_DEAL.documentCount))}
        </span>
      </div>
      <div className="border-b border-[rgba(20,36,46,.12)]">
        {documents.map((doc) => (<div key={doc.title} className="flex items-center gap-3 border-t border-ds-rule-row py-[9px]">
            <PdfFileMark width={21} height={24}/>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-semibold leading-[1.3] text-ds-ink">{doc.title}</div>
              <div className="ds-meta mt-[2px] leading-[1.4]">
                {doc.source} <span className="whitespace-nowrap">{dealDate}</span>
              </div>
            </div>
            <DownloadMark className="shrink-0 text-ds-muted" width={16} height={16}/>
          </div>))}
      </div>

      
      <div className="mt-6 flex items-baseline justify-between gap-3 pb-[5px]">
        <span className="text-[15px] font-semibold text-ds-ink-strong">{t("history.title")}</span>
        <span className="ds-meta">
          {t("ds.transaction.count.many").replace("{count}", String(dealCount))}
        </span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-t border-ds-rule-row py-[9px]">
        <div className="min-w-0 flex-1 basis-[190px]">
          <div className="text-[14.5px] font-semibold leading-[1.3] text-ds-ink">
            {t("ds.transaction.boughtFrom").replace("{counterparty}", HERO_DEAL.seller)}
          </div>
          <div className="ds-meta mt-[2px] leading-[1.4]">
            {t("landing.afterDeal.history.latest")} ·{" "}
            <span className="whitespace-nowrap">{dealDate}</span>
          </div>
        </div>
        <div className="ml-auto flex-none text-[14.5px] font-semibold tabular-nums text-ds-ink">
          {HERO_DEAL.price}
        </div>
      </div>
    </div>);
};
const LandingPreLogin = () => {
    const { t } = useLanguage();
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [entryIntent, setEntryIntent] = useState("login");
    const [traceDrawn, setTraceDrawn] = useState(false);
    useEffect(() => {
        const id = window.setTimeout(() => setTraceDrawn(true), 400);
        return () => window.clearTimeout(id);
    }, []);
    const heroCtaRef = useRef(null);
    const [heroCtaPassed, setHeroCtaPassed] = useState(false);
    useEffect(() => {
        const node = heroCtaRef.current;
        if (!node || typeof IntersectionObserver === "undefined") {
            setHeroCtaPassed(true);
            return;
        }
        const observer = new IntersectionObserver(([entry]) => setHeroCtaPassed(!entry.isIntersecting), { rootMargin: "-60px 0px 0px 0px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);
    const ledgerRef = useRef(null);
    const [ledgerArmed] = useState(() => typeof window !== "undefined" &&
        typeof IntersectionObserver !== "undefined" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    const [ledgerRuled, setLedgerRuled] = useState(false);
    useEffect(() => {
        if (!ledgerArmed || ledgerRuled)
            return;
        const node = ledgerRef.current;
        if (!node) {
            setLedgerRuled(true);
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setLedgerRuled(true);
                observer.disconnect();
            }
        }, { rootMargin: "0px 0px -55% 0px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [ledgerArmed, ledgerRuled]);
    const [scrolled, setScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const openEntry = (intent) => {
        setEntryIntent(intent);
        setIsLoginOpen(true);
        setIsMobileMenuOpen(false);
    };
    const lastScrollTime = useRef(0);
    useEffect(() => {
        const handleScroll = () => {
            const now = Date.now();
            if (now - lastScrollTime.current < SCROLL_THROTTLE_MS)
                return;
            lastScrollTime.current = now;
            setScrolled(window.scrollY > 16);
        };
        window.addEventListener("scroll", handleScroll, {
            passive: true
        });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === "Escape" && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };
        window.addEventListener("keydown", handleEscape);
        return () => window.removeEventListener("keydown", handleEscape);
    }, [isMobileMenuOpen]);
    useEffect(() => {
        const id = window.location.hash.slice(1);
        if (!id)
            return;
        const frame = requestAnimationFrame(() => {
            document.getElementById(id)?.scrollIntoView({ behavior: "auto" });
        });
        return () => cancelAnimationFrame(frame);
    }, []);
    const supportedVehicles = [{
            name: t("Bil"),
            visual: "car"
        }, {
            name: t("MC"),
            visual: "motorcycle"
        }, {
            name: t("Moped"),
            visual: "moped"
        }];
    const roles = [{
            label: t("Köpare"),
            accent: "var(--ds-buyer)",
            labelColor: "var(--ds-buyer-fg)",
            title: t("landing.roles.buyer.title"),
            body: t("landing.roles.buyer.body"),
            support: t("landing.roles.buyer.steps"),
            cta: t("Kom igång som köpare"),
            event: "cta_kom_igang_kopare",
            intent: "buyer"
        }, {
            label: t("Säljare"),
            accent: "var(--ds-seller)",
            labelColor: "var(--ds-seller-fg)",
            title: t("landing.roles.seller.title"),
            body: t("landing.roles.seller.body"),
            support: t("landing.roles.seller.steps"),
            cta: t("Kom igång som säljare"),
            event: "cta_kom_igang_saljare",
            intent: "seller"
        }];
    const processStages = [{
            title: t("Skapa köpekontraktet"),
            description: t("Fordon, pris och villkor samlas i ett digitalt köpekontrakt som ni kan gå igenom och ändra vid behov innan signering.")
        }, {
            title: t("Signera med BankID"),
            description: t("När ni är överens signerar ni båda köpekontraktet med BankID.")
        }, {
            title: t("Betala med Swish"),
            description: t("Betalningen sker med Swish direkt mellan köpare och säljare och hålls ihop med resten av affären i Klargo.")
        }, {
            title: t("Slutför affären"),
            description: t("Klargo guidar er genom ägarbytet hos Transportstyrelsen. Efteråt finns affären och dokumenten kvar samlade i Klargo.")
        }];
    const faqItems = [{
            question: t("När får säljaren pengarna?"),
            answer: t("Pengarna går direkt från köparen till säljaren. Med Swish kommer de fram på en gång. Säljaren bekräftar i Klargo när de har kommit fram.")
        }, {
            question: t("Vad gäller för Swish-beloppsgränsen?"),
            answer: t("Din bank bestämmer hur mycket du kan swisha. Kontrollera gränsen före affären och höj den vid behov i din bankapp.")
        }, {
            question: t("Hur vet jag vem jag gör affären med?"),
            answer: t("Både köpare och säljare legitimerar sig med BankID i affären. Köpekontraktet signeras med samma BankID, så det står klart vilka som ingått avtalet.")
        }, {
            question: t("När behöver försäkringen börja gälla?"),
            answer: t("Om fordonet är påställt ska den nya ägaren ha en trafikförsäkring som gäller från ägarbytesdatumet. Är fordonet avställt behövs trafikförsäkring först när det ställs på. Du väljer själv försäkringsbolag och kan jämföra alternativ via Zmarta i Klargo.")
        }, {
            question: t("Hur går ägarbytet till?"),
            answer: t("Ägarbytet anmäls hos Transportstyrelsen. Klargo guidar er genom det sista steget och visar vilka uppgifter som behövs, så att det blir rätt.")
        }, {
            question: t("Vad händer om det finns lån på fordonet?"),
            answer: t("Ni kommer överens om hur lånet ska lösas. Klargo guidar er genom det och dokumenterar överenskommelsen i affären.")
        }, {
            question: t("Vad kostar det?"),
            answer: t("Ingenting. Klargo är gratis för både köpare och säljare.")
        }];
    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({
                behavior: "smooth"
            });
            setIsMobileMenuOpen(false);
        }
    };
    return <div className="ds-scope ds-public-anchors min-h-screen bg-ds-canvas font-ds-sans text-[15px] leading-[1.55] text-ds-body antialiased">
      
      <Helmet>
        <title>{t("landing.docTitle")}</title>
        <meta name="description" content={t("landing.docDescription")}/>
      </Helmet>
      
      <nav role="navigation" aria-label={t("nav.publicLabel")} className={`sticky top-0 z-50 border-b border-ds-rule-row bg-ds-sheet transition-shadow duration-200 ${scrolled ? "shadow-[0_1px_0_rgba(20,36,46,.08),0_6px_16px_-10px_rgba(16,30,40,.18)]" : ""}`}>
        <div className="mx-auto max-w-[1200px] px-5 md:px-8">
          <div className="flex h-[60px] items-center justify-between">
            
            <div className="flex items-center">
              <KlargoLogo variant="header" ariaLabel={t("nav.logoHome")} onClick={() => window.scrollTo({
            top: 0,
            behavior: 'smooth'
        })}/>
            </div>

            



            
            <div className="flex items-center gap-2">
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
              <button type="button" onClick={() => openEntry("login")} className="press hidden h-10 items-center rounded-[9px] bg-ds-ink px-[18px] text-[14px] font-semibold text-ds-ink-on hover:bg-ds-ink-hover sm:inline-flex" data-event="cta_login_nav">
                {t("Logga in")}
              </button>

              
              
              <button type="button" onClick={() => openEntry("start")} aria-hidden={!heroCtaPassed} tabIndex={heroCtaPassed ? undefined : -1} className={`press relative inline-flex h-9 items-center rounded-[9px] bg-ds-ink px-3.5 text-[13.5px] font-semibold text-ds-ink-on before:absolute before:-inset-y-1 before:inset-x-0 before:content-[''] hover:bg-ds-ink-hover sm:hidden ${heroCtaPassed ? "visible opacity-100" : "invisible opacity-0"}`} data-event="cta_kom_igang_nav" data-testid="nav-start-cta">
                {t("Kom igång")}
              </button>

              
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="relative rounded-lg p-2 text-ds-text transition-colors before:absolute before:-inset-1 before:content-[''] hover:text-ds-ink md:hidden" aria-label={isMobileMenuOpen ? t("Stäng meny") : t("Öppna meny")} aria-expanded={isMobileMenuOpen} aria-controls="mobile-menu">
                {isMobileMenuOpen ? <X className="h-5 w-5"/> : <Menu className="h-5 w-5"/>}
              </button>
            </div>
          </div>

          
          {isMobileMenuOpen && <div id="mobile-menu" className="space-y-1 border-t border-ds-rule-row pb-4 pt-2 md:hidden">
              <div className="flex items-center justify-between">
                <LanguageSwitcher />
                
                <button type="button" onClick={() => openEntry("login")} className="press inline-flex h-10 items-center rounded-[9px] border border-[color:var(--ds-rule-control)] px-[18px] text-[14px] font-semibold text-ds-ink hover:bg-[rgba(20,36,46,.05)]" data-event="cta_login_mobile">
                  {t("Logga in")}
                </button>
              </div>
            </div>}
        </div>
      </nav>

      
      <section id="hero" className="relative overflow-hidden">
        
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0" style={{
            maskImage: "linear-gradient(to bottom, #000 0%, #000 68%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 68%, rgba(0,0,0,0) 100%)",
        }}>
            <div className="absolute right-[-18%] top-[2%] hidden h-[860px] w-[1020px] md:block" style={{
            background: "radial-gradient(ellipse 58% 56% at 58% 46%, rgba(11,122,85,.07), rgba(11,122,85,.028) 46%, rgba(11,122,85,0) 76%)"
        }}/>
            
            <GhostRecord className="right-[-9%] top-[-15%] hidden h-[430px] w-[540px] rotate-[6deg] lg:block"/>
            <GhostRecord className="bottom-[-150px] right-[-8%] hidden h-[440px] w-[560px] -rotate-[7deg] lg:block"/>
            <GhostRecord className="left-[63%] top-[-26%] hidden h-[340px] w-[420px] -rotate-[4deg] xl:block"/>
            
            
          </div>
          
          <TransactionTrace drawn={traceDrawn} className="absolute bottom-[56px] right-[-4%] hidden h-[168px] w-[440px] lg:block"/>
        </div>
        <div className="relative mx-auto max-w-[1200px] px-6 pb-10 pt-12 md:px-8 md:pb-12 md:pt-[clamp(44px,7vh,64px)] min-[1600px]:max-w-[1380px]">
          <div className="hero-grid">
            
            <div className="hero-copy-top max-w-[620px]">

              
              
              
              <h1 className="m-0 font-ds-serif text-[clamp(28px,4.8vw,42px)] font-bold leading-[1.08] tracking-[-0.02em] max-[374px]:text-[26px] max-[339px]:text-[24px] min-[1600px]:text-[52px] text-ds-ink">
                {t("En trygg fordonsaffär")}
                <span className="block [text-wrap:balance] md:hidden">{t("hero.h1.line2.mobile")}</span>
                <span className="hidden [text-wrap:balance] md:block">{t("från kontrakt till ägarbyte.")}</span>
              </h1>


              
              <p className="mb-0 mt-[18px] max-w-[54ch] text-[16.5px] leading-[1.6] text-ds-text md:hidden">
                {t("Köpare och säljare gör affären tillsammans: kontrakt signerat med BankID, betalning med Swish och ägarbyte steg för steg.")}
              </p>
              <p className="mb-0 mt-[18px] hidden max-w-[54ch] text-[16.5px] leading-[1.6] text-ds-text md:block">
                {t("Köpare och säljare gör affären tillsammans i Klargo: köpekontrakt som signeras med BankID, betalning med Swish och ägarbyte steg för steg. Köparen kan jämföra och teckna försäkring via Zmarta i samma flöde.")}
              </p>

              
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <button ref={heroCtaRef} type="button" onClick={() => openEntry("start")} className="ds-action-ink justify-center whitespace-nowrap" data-event="cta_kom_igang_hero">
                  <span>{t("Kom igång")}</span>
                  <ForwardArrow />
                </button>
                <button type="button" onClick={() => scrollToSection("hur-det-funkar")} className="ds-action-outline hidden justify-center whitespace-nowrap sm:inline-flex" data-event="cta_se_hur_det_funkar">
                  {t("Se hur det funkar")}
                </button>
              </div>

              
              <p className="mb-0 mt-3.5 text-[13.5px] font-medium text-ds-text">
                {t("Gratis för både köpare och säljare.")}
              </p>

              
              
            </div>

            
            <div className="hero-copy-bottom max-w-[620px]">
              
              
              <ul className="m-0 mt-3 list-none border-t border-[rgba(20,36,46,.22)] p-0 pt-[13px] md:mt-7">

                {[
            t("Juristgranskat köpekontrakt"),
            t("Fordonsuppgifter från Vägtrafikregistret"),
            t("Data lagras i Sverige"),
        ].map((proof) => (<li key={proof} className="flex items-start gap-2.5 py-[5px] text-[13.5px] leading-[1.45] text-ds-text">
                    <span className="mt-[6px] flex-none">
                      <VerifiedTick />
                    </span>
                    <span>{proof}</span>
                  </li>))}
              </ul>

              
              <ServiceLogoRow label={t("Tjänster och datakällor i affären")}/>
            </div>

            
            <div className="hero-visual">
              <HeroArtifacts mobileFooter={<button type="button" onClick={() => scrollToSection("hur-det-funkar")} className="mt-1 inline-flex min-h-[44px] items-center gap-2 text-[14.5px] font-semibold text-ds-ink sm:hidden" data-event="cta_se_hur_det_funkar">
                    <span>{t("Se hur det funkar")}</span>
                    <ForwardArrow stroke="var(--ds-ink)" width={14} height={10}/>
                  </button>}/>
            </div>
          </div>
        </div>

        
        <div id="vehicle-types" className="relative mx-auto max-w-[1200px] px-6 pb-10 md:px-8 md:pb-12 min-[1600px]:max-w-[1380px]">
          <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="font-ds-serif text-[16px] font-semibold leading-[1.25] text-ds-ink">
                {t("För:")}
              </span>
              {supportedVehicles.map((vehicle) => (<span key={vehicle.name} className="inline-flex items-center gap-2 text-[13px] font-medium text-ds-body">
                  <VehicleTypeVisual kind={vehicle.visual} className="w-10 flex-none"/>
                  {vehicle.name}
                </span>))}
            </div>
            <p className="mb-0 text-[13px] leading-[1.5] text-ds-muted">
              {t("Husbil och lätt lastbil kommer snart.")}
            </p>
          </div>
        </div>
      </section>

      
      <section id="choose-role" className="py-14 md:py-16">
        <div className="mx-auto max-w-[1200px] px-6 md:px-8">
          
          <SectionHeader kicker={t("För dig som köper eller säljer privat")} title={t("Tryggare på båda sidor av affären")} lead={t("landing.roles.lead")}/>

          <div className="grid items-stretch gap-5 md:grid-cols-2">
            {roles.map((role) => (<div key={role.label} className="landing-role flex min-w-0 flex-col rounded-[10px] border border-ds-rule-edge bg-ds-sheet px-[26px] pb-[26px] pt-[23px]" style={{ '--role-accent': role.accent }}>
                <span className="text-[13px] font-semibold tracking-[0.01em]" style={{ color: role.labelColor }}>
                  {role.label}
                </span>
                <h3 className="mb-0 mt-[13px] font-ds-serif text-[clamp(22px,2.1vw,26px)] font-bold leading-[1.15] tracking-[-0.01em] text-ds-ink">
                  {role.title}
                </h3>
                
                <p className="mb-7 mt-3 max-w-[46ch] text-[15px] leading-[1.55] text-ds-text [text-wrap:pretty]">
                  {role.body}
                </p>

                
                <div className="mt-auto border-t border-ds-rule-row pt-4">
                  <p className="mb-0 text-[12.5px] leading-[1.6] tracking-[0.01em] text-ds-muted [text-wrap:pretty]">
                    {role.support}
                  </p>
                  <button type="button" onClick={() => openEntry(role.intent)} className="ds-action-ink mt-6 whitespace-nowrap" data-event={role.event}>
                    <span>{role.cta}</span>
                    <ForwardArrow />
                  </button>
                </div>
              </div>))}
          </div>
        </div>
      </section>

      
      <section id="hur-det-funkar" className="relative scroll-mt-20 overflow-hidden py-14 md:py-16">
        
        <div aria-hidden="true" className="pointer-events-none absolute bottom-[-40px] left-[-180px] hidden h-[440px] w-[900px] md:block" style={{
            background: "radial-gradient(ellipse 50% 48% at 50% 50%, rgba(11,122,85,.05), rgba(11,122,85,.018) 44%, rgba(11,122,85,0) 74%)"
        }}/>
        <div className="relative mx-auto max-w-[1200px] px-6 md:px-8">
          <SectionHeader kicker={t("Så fungerar Klargo")} title={t("Hela fordonsaffären samlad i ett flöde")}/>

          <div ref={ledgerRef} data-ledger={ledgerArmed ? (ledgerRuled ? "ruled" : "pending") : undefined} className="landing-ledger grid border-t-2 border-ds-ink md:grid-cols-2 lg:grid-cols-4">
            {processStages.map((stage, index) => (<div key={stage.title} className={`flex flex-col border-ds-rule-row pb-6 pt-5 ${STAGE_RULES[index]}`}>
                <span className="font-ds-mono text-[13px] font-medium tracking-[0.08em] text-ds-muted">
                  {`0${index + 1}`}
                </span>
                <h3 className="mb-0 mt-2.5 font-ds-serif text-[19px] font-semibold leading-[1.25] tracking-[-0.005em] text-ds-ink">
                  {stage.title}
                </h3>
                <p className="mb-0 mt-2 max-w-[34ch] text-[14.5px] leading-[1.55] text-ds-text [text-wrap:pretty]">
                  {stage.description}
                </p>
              </div>))}
          </div>

          
          <div className="mt-7 max-w-[70ch]">
            <div className="mb-1 text-[13px] font-semibold tracking-[0.02em] text-ds-ink">
              {t("Försäkring i samma flöde")}
            </div>
            <p className="mb-0 text-[13.5px] leading-[1.55] text-ds-muted [text-wrap:pretty]">
              {t("Köparen kan jämföra försäkringar från flera bolag via Zmarta och teckna direkt i Klargo.")}
            </p>
          </div>
        </div>
      </section>

      
      <section id="efter-affaren" className="relative scroll-mt-20 overflow-hidden border-y border-ds-rule-row bg-ds-sheet py-12 md:py-20">
        
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
            background: "linear-gradient(112deg, rgba(11,122,85,0) 42%, rgba(11,122,85,.035) 66%, rgba(31,160,92,.025) 82%, rgba(11,122,85,0) 97%)"
        }}/>
        <div className="relative mx-auto max-w-[1200px] px-6 md:px-8">
          
          <div className="grid items-start gap-8 md:gap-10 lg:grid-cols-[minmax(0,400px)_minmax(0,1fr)] lg:gap-14">
            <div className="max-w-[640px] lg:pt-[74px]">
              <div className="mb-1.5 text-[13px] font-semibold tracking-[0.02em] text-ds-muted">
                {t("Efter affären")}
              </div>
              <h2 className="m-0 font-ds-serif text-[clamp(24px,2.6vw,30px)] font-bold leading-[1.12] tracking-[-0.01em] text-ds-ink">
                {t("Fordonet och affären finns kvar i Klargo")}
              </h2>
              <p className="mb-0 mt-4 text-[15px] leading-[1.55] text-ds-text [text-wrap:pretty]">
                {t("Efter ett genomfört köp finns fordonet kvar i Klargo med viktiga uppgifter och handlingar samlade.")}
              </p>
              <p className="mb-0 mt-3 text-[15px] leading-[1.55] text-ds-text [text-wrap:pretty]">
                {t("Köpekontrakt, besiktningsprotokoll och försäkringsbrev ligger kvar på fordonet, och den genomförda affären sparas i Affärshistorik.")}
              </p>
            </div>

            
            <div className="hidden md:block" data-testid="after-deal-desktop">
              <AfterDealArtifact />
            </div>
            
            <div className="md:hidden">
              <AfterDealMobile />
            </div>
          </div>
        </div>
      </section>

      
      <section id="faq" className="scroll-mt-20 py-14 md:py-16">
        <div className="mx-auto max-w-[1200px] px-6 md:px-8">
          
          
          
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,640px)] lg:grid-rows-[auto_1fr] lg:items-start lg:gap-x-14 lg:gap-y-9">
            <SectionHeader kicker={t("landing.faq.kicker")} title={t("Frågor och svar")} className="lg:col-start-1 lg:row-start-1 lg:mb-0"/>

            
            
            <Accordion type="single" collapsible className="max-w-[640px] border-t-2 border-ds-ink lg:col-start-2 lg:row-start-1 lg:row-span-2">
              {faqItems.map((item, index) => <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-ds-rule-row">
                  
                  <AccordionTrigger className="items-start gap-6 py-[19px] text-left data-[state=open]:pb-[12px] hover:no-underline [&>svg]:mt-[4px] [&>svg]:h-[14px] [&>svg]:w-[14px] [&>svg]:stroke-[1.6] [&>svg]:text-ds-muted">
                    <span className="font-ds-serif text-[16px] font-semibold leading-[1.35] text-ds-ink md:text-[17px]">{item.question}</span>
                  </AccordionTrigger>
                  
                  <AccordionContent className="max-w-[62ch] pb-[22px] pr-6 text-[15px] leading-[1.6] text-ds-text">{item.answer}</AccordionContent>
                </AccordionItem>)}
            </Accordion>

            
            <div className="mt-9 max-w-[640px] border-t border-ds-rule-row pt-4 lg:col-start-1 lg:row-start-2 lg:mt-0 lg:max-w-[300px]">
              <p className="m-0 text-[14.5px] font-medium leading-[1.55] text-ds-ink">
                {t("landing.faq.more")}
              </p>
              <div className="mt-0.5 flex flex-col items-start">
                <Link to="/sakerhet-och-bedragerier" className="ds-action-text">
                  {t("footer.security")}
                  <ForwardArrow stroke="currentColor" width={14} height={11}/>
                </Link>
                <Link to="/hjalp" className="ds-action-text">
                  {t("nav.helpContact")}
                  <ForwardArrow stroke="currentColor" width={14} height={11}/>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      
      <section id="kom-igang" className="scroll-mt-20 border-y border-ds-rule-row bg-ds-sheet py-14 md:py-16">
        <div className="mx-auto max-w-[1200px] px-6 md:px-8">
          <div className="border-t-2 border-ds-ink pt-7 md:flex md:items-end md:justify-between md:gap-14">
            
            <div className="max-w-[600px]">
              <h2 className="m-0 font-ds-serif text-[clamp(26px,2.9vw,34px)] font-bold leading-[1.1] tracking-[-0.015em] text-ds-ink">
                {t("Gör nästa fordonsaffär i Klargo")}
              </h2>
              
              <p className="mb-0 mt-3.5 text-[15.5px] leading-[1.55] text-ds-text [text-wrap:pretty]">
                {t("Från överenskommelse till genomförd affär.")}{" "}
                <span className="font-medium text-ds-ink">{t("Gratis för både köpare och säljare.")}</span>
              </p>
            </div>

            
            <div className="mt-7 flex md:mt-0 md:ml-auto md:flex-none md:pb-[3px]">
              <button type="button" onClick={() => openEntry("start")} className="ds-action-ink w-full justify-center whitespace-nowrap sm:w-auto" data-event="cta_kom_igang_final">
                <span>{t("Kom igång")}</span>
                <ForwardArrow />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer ground="canvas"/>

      
      <LoginDialog open={isLoginOpen} onOpenChange={setIsLoginOpen} intent={entryIntent} resumePending/>
    </div>;
};
export default LandingPreLogin;
